import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import {
  SquaresFour, Users, Images, FileText, Funnel,
  Gear, SignOut, Leaf, List, X, Database, BellSimple, CaretDown, Newspaper, PenNib,
} from "@phosphor-icons/react";

function SidebarContent({ menuItems, location, onLinkClick, onLogout }) {
  // Auto-expand any group whose child matches current path
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const init = {};
    menuItems.forEach((item) => {
      if (item.children && item.children.some((c) => location.pathname === c.path)) {
        init[item.label] = true;
      }
    });
    return init;
  });

  const toggleGroup = (label) => {
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: "#1A5C37" }}>
      {/* Logo */}
      <div className="flex items-center gap-3 px-6 py-5 border-b" style={{ borderColor: "rgba(247, 245, 240, 0.1)" }}>
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(247, 245, 240, 0.15)" }}>
          <Leaf size={16} weight="duotone" style={{ color: "#F7F5F0" }} />
        </div>
        <span className="font-heading text-lg font-semibold tracking-tight" style={{ color: "#F7F5F0" }}>KYBIA Admin</span>
        {onLinkClick && (
          <button className="ml-auto" onClick={onLinkClick} style={{ color: "rgba(247,245,240,0.6)" }}>
            <X size={20} weight="bold" />
          </button>
        )}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {menuItems.map((item) => {
          /* ── Group item (has children) ── */
          if (item.children) {
            const isExpanded = !!expandedGroups[item.label];
            const hasActiveChild = item.children.some((c) => location.pathname === c.path);
            const Icon = item.icon;
            return (
              <div key={item.label}>
                <button
                  onClick={() => toggleGroup(item.label)}
                  className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150"
                  style={{
                    backgroundColor: hasActiveChild ? "rgba(247, 245, 240, 0.15)" : "transparent",
                    color: hasActiveChild ? "#F7F5F0" : "rgba(247, 245, 240, 0.65)",
                  }}
                  onMouseEnter={(e) => {
                    if (!hasActiveChild) {
                      e.currentTarget.style.backgroundColor = "rgba(247, 245, 240, 0.08)";
                      e.currentTarget.style.color = "#F7F5F0";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!hasActiveChild) {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = "rgba(247, 245, 240, 0.65)";
                    }
                  }}
                >
                  <Icon size={18} weight={hasActiveChild ? "duotone" : "regular"} />
                  <span className="flex-1 text-left">{item.label}</span>
                  <CaretDown
                    size={13}
                    weight="bold"
                    style={{
                      transition: "transform 0.2s ease",
                      transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                      opacity: 0.7,
                    }}
                  />
                </button>

                {/* Sub-items */}
                <div
                  style={{
                    overflow: "hidden",
                    maxHeight: isExpanded ? `${item.children.length * 44}px` : "0px",
                    transition: "max-height 0.2s ease",
                  }}
                >
                  <div className="pl-4 pt-0.5 pb-1 space-y-0.5">
                    {item.children.map((child) => {
                      const isChildActive = location.pathname === child.path;
                      return (
                        <Link
                          key={child.path}
                          to={child.path}
                          onClick={() => onLinkClick && onLinkClick()}
                          className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors duration-150"
                          style={{
                            backgroundColor: isChildActive ? "rgba(247, 245, 240, 0.12)" : "transparent",
                            color: isChildActive ? "#F7F5F0" : "rgba(247, 245, 240, 0.55)",
                          }}
                          onMouseEnter={(e) => {
                            if (!isChildActive) {
                              e.currentTarget.style.backgroundColor = "rgba(247, 245, 240, 0.07)";
                              e.currentTarget.style.color = "#F7F5F0";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isChildActive) {
                              e.currentTarget.style.backgroundColor = "transparent";
                              e.currentTarget.style.color = "rgba(247, 245, 240, 0.55)";
                            }
                          }}
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: isChildActive ? "#F7F5F0" : "rgba(247,245,240,0.35)" }}
                          />
                          {child.label}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          }

          /* ── Regular item ── */
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => onLinkClick && onLinkClick()}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150"
              style={{
                backgroundColor: isActive ? "rgba(247, 245, 240, 0.15)" : "transparent",
                color: isActive ? "#F7F5F0" : "rgba(247, 245, 240, 0.65)",
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = "rgba(247, 245, 240, 0.08)";
                  e.currentTarget.style.color = "#F7F5F0";
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "rgba(247, 245, 240, 0.65)";
                }
              }}
            >
              <Icon size={18} weight={isActive ? "duotone" : "regular"} />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="px-3 py-4 border-t" style={{ borderColor: "rgba(247, 245, 240, 0.1)" }}>
        <button
          onClick={onLogout}
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150"
          style={{ color: "rgba(247, 245, 240, 0.65)" }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(247, 245, 240, 0.08)"; e.currentTarget.style.color = "#F7F5F0"; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "rgba(247, 245, 240, 0.65)"; }}
        >
          <SignOut size={18} />
          Sign Out
        </button>
      </div>
    </div>
  );
}

export default function AdminLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, admin } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/admin/login"); };

  const menuItems = [
    { label: "Dashboard", path: "/admin/dashboard", icon: SquaresFour, show: true },
    { label: "Admins", path: "/admin/admins", icon: Users, show: admin?.is_superadmin },
    {
      label: "Images",
      icon: Images,
      show: true,
      children: [
        { label: "Banner", path: "/admin/banner" },
        { label: "Gallery", path: "/admin/gallery" },
        { label: "Timeline Pictures", path: "/admin/timeline-pictures" },
      ],
    },
    { label: "Content", path: "/admin/content", icon: FileText, show: true },
    { label: "Pop-ups", path: "/admin/popups", icon: BellSimple, show: true },
    { label: "News", path: "/admin/news", icon: Newspaper, show: true },
    { label: "KYBIA Reports", path: "/admin/kybia-reports", icon: FileText, show: true },
    { label: "Reports", path: "/admin/reports", icon: FileText, show: true },
    {
      label: "Master Data",
      icon: Database,
      show: true,
      children: [
        { label: "Report Categories", path: "/admin/master-data/report-categories" },
      ],
    },
    { label: "Leads", path: "/admin/leads", icon: Funnel, show: admin?.show_leads !== false },
    { label: "Petitions", path: "/admin/petitions", icon: PenNib, show: true },
    { label: "Settings", path: "/admin/settings", icon: Gear, show: true },
  ].filter((m) => m.show);

  // Resolve page label for the topbar — check flat items + group children
  const currentLabel = (() => {
    for (const item of menuItems) {
      if (item.path && item.path === location.pathname) return item.label;
      if (item.children) {
        const child = item.children.find((c) => c.path === location.pathname);
        if (child) return child.label;
      }
    }
    return "Admin";
  })();

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: "#F7F5F0" }}>
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-60 flex-shrink-0">
        <SidebarContent menuItems={menuItems} location={location} onLogout={handleLogout} />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 md:hidden" onClick={() => setSidebarOpen(false)} style={{ backgroundColor: "rgba(0,0,0,0.4)" }} />
      )}
      <div className={`fixed inset-y-0 left-0 z-50 w-60 md:hidden transform transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <SidebarContent menuItems={menuItems} location={location} onLinkClick={() => setSidebarOpen(false)} onLogout={handleLogout} />
      </div>

      {/* Main */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <header className="flex items-center justify-between px-6 h-16 border-b flex-shrink-0" style={{ backgroundColor: "#fff", borderColor: "rgba(26, 92, 55, 0.1)" }}>
          <div className="flex items-center gap-3">
            <button className="md:hidden p-1.5" onClick={() => setSidebarOpen(true)} style={{ color: "#1A5C37" }}>
              <List size={22} weight="bold" />
            </button>
            <h2 className="font-heading text-lg font-medium" style={{ color: "#1C211F" }}>
              {currentLabel}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: "rgba(26,92,55,0.1)", color: "#1A5C37" }}>
              {admin?.name?.[0]?.toUpperCase() || "A"}
            </div>
            <span className="text-xs hidden sm:block" style={{ color: "#4A5450" }}>{admin?.email}</span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 md:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
